import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../api/client';

interface OrderDot {
  id: string;
  orderNumber: string | null;
  orderStatus: string | null;
  contactPerson: string | null;
  orderStartDate: string;
  orderEndDate: string | null;
  serviceTypeName: string | null;
  customerName: string | null;
  orderAmount: number | null;
}

const STATUS_DOT: Record<string, string> = {
  '未执行': 'bg-amber-400',
  '执行中': 'bg-cyan-400',
  '已完成': 'bg-teal-400',
  // 后端已过滤掉已取消/已拒绝，这里降级为中性灰色，作为兄底容错不引起视觉干扰
  '已取消': 'bg-gray-300',
  '已拒绝': 'bg-gray-300',
  '已归档': 'bg-gray-300',
};

const STATUS_BADGE: Record<string, string> = {
  '未执行': 'bg-amber-50 text-amber-600 border-amber-200',
  '执行中': 'bg-cyan-50 text-cyan-600 border-cyan-200',
  '已完成': 'bg-teal-50 text-teal-600 border-teal-200',
  '已取消': 'bg-gray-100 text-gray-400 border-gray-200',
  '已拒绝': 'bg-gray-100 text-gray-400 border-gray-200',
  '已归档': 'bg-gray-100 text-gray-500 border-gray-200',
};

const MONTH_NAMES = ['一月','二月','三月','四月','五月','六月','七月','八月','九月','十月','十一月','十二月'];
const WEEK_DAYS = ['日','一','二','三','四','五','六'];

export default function Calendar() {
  const navigate = useNavigate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [currentDate, setCurrentDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [ordersByDate, setOrdersByDate] = useState<Record<string, OrderDot[]>>({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const toDateStr = (y: number, m: number, d: number) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  // 展开多天订单：将订单从 start 到 end 的每一天都打点
  // 注意：使用本地日期格式化而非 toISOString()，避免东八区下变成 UTC 偏移到前一天
  const expandOrderToDates = useCallback(
    (order: OrderDot, monthFrom: string, monthTo: string): string[] => {
      const start = order.orderStartDate;
      const end = order.orderEndDate ?? start;
      // 限制在当月范围内展开
      const from = start < monthFrom ? monthFrom : start;
      const to = end > monthTo ? monthTo : end;
      const dates: string[] = [];
      const cur = new Date(from + 'T00:00:00');
      const last = new Date(to + 'T00:00:00');
      while (cur <= last) {
        // 用本地日期字段拼 YYYY-MM-DD，与月历格子的 dateStr 保持一致
        const y = cur.getFullYear();
        const m = String(cur.getMonth() + 1).padStart(2, '0');
        const d = String(cur.getDate()).padStart(2, '0');
        dates.push(`${y}-${m}-${d}`);
        cur.setDate(cur.getDate() + 1);
      }
      return dates;
    },
    []
  );

  const fetchOrders = useCallback(async (year: number, month: number) => {
    setLoading(true);
    try {
      const from = toDateStr(year, month, 1);
      const lastDay = new Date(year, month + 1, 0).getDate();
      const to = toDateStr(year, month, lastDay);

      const data = await get<OrderDot[]>('/orders/calendar', {
        params: {
          dateFrom: from,
          dateTo: to
        }
      });

      const map: Record<string, OrderDot[]> = {};

      (data ?? []).forEach((o) => {
        // 展开到每一天
        const dates = expandOrderToDates(o, from, to);
        dates.forEach(dateKey => {
          if (!map[dateKey]) map[dateKey] = [];
          // 防止同一订单重复添加
          if (!map[dateKey].some(existing => existing.id === o.id)) {
            map[dateKey].push(o);
          }
        });
      });

      setOrdersByDate(map);
    } catch (error) {
      console.error('获取日历订单失败:', error);
    } finally {
      setLoading(false);
    }
  }, [expandOrderToDates]);

  useEffect(() => {
    fetchOrders(currentDate.getFullYear(), currentDate.getMonth());
    setSelectedDate(null);
  }, [currentDate, fetchOrders]);

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;

  const previousMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const handleDayClick = (dayNum: number) => {
    const dateStr = toDateStr(currentDate.getFullYear(), currentDate.getMonth(), dayNum);
    setSelectedDate(prev => (prev === dateStr ? null : dateStr));
  };

  const selectedOrders = selectedDate ? (ordersByDate[selectedDate] ?? []) : [];

  // 判断某天是否是订单的开始日、中间日还是结束日（用于颜色区分）
  const getDayRole = (order: OrderDot, dateStr: string): 'start' | 'mid' | 'end' | 'single' => {
    const isMultiDay = order.orderEndDate && order.orderEndDate !== order.orderStartDate;
    if (!isMultiDay) return 'single';
    if (dateStr === order.orderStartDate) return 'start';
    if (dateStr === order.orderEndDate) return 'end';
    return 'mid';
  };

  const renderDays = () => {
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - firstDayOfMonth + 1;
      const isCurrentMonth = dayNum > 0 && dayNum <= daysInMonth;
      const dateStr = isCurrentMonth
        ? toDateStr(currentDate.getFullYear(), currentDate.getMonth(), dayNum)
        : '';
      const isToday = isCurrentMonth && dateStr === toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
      const isSelected = isCurrentMonth && dateStr === selectedDate;
      const dayOrders = dateStr ? (ordersByDate[dateStr] ?? []) : [];
      const hasOrders = dayOrders.length > 0;

      // 检测是否有跨天订单（多天活动中间日，非开始日）
      const hasMultiDayMid = hasOrders && dayOrders.some(o =>
        o.orderEndDate && o.orderEndDate !== o.orderStartDate && dateStr > o.orderStartDate
      );
      const hasMultiDayStart = hasOrders && dayOrders.some(o =>
        o.orderEndDate && o.orderEndDate !== o.orderStartDate && dateStr === o.orderStartDate
      );

      cells.push(
        <div
          key={i}
          onClick={() => isCurrentMonth && handleDayClick(dayNum)}
          className={`relative flex flex-col items-center justify-start pt-1 pb-0.5 rounded-lg transition-all min-h-[44px] ${
            isCurrentMonth ? 'cursor-pointer' : 'pointer-events-none'
          } ${
            isSelected
              ? 'bg-teal-500 text-white'
              : isToday
              ? 'bg-teal-50 ring-1 ring-teal-400 text-teal-700 font-semibold'
              : isCurrentMonth
              ? 'hover:bg-gray-50 text-gray-900'
              : 'text-gray-200'
          }`}
        >
          <span className="text-sm leading-none">{isCurrentMonth ? dayNum : ''}</span>
          {hasOrders && isCurrentMonth && (
            <div className="flex items-center gap-0.5 mt-1 flex-wrap justify-center px-0.5">
              {dayOrders.slice(0, 3).map((o, idx) => {
                const role = getDayRole(o, dateStr);
                const dotColor = isSelected ? 'bg-white/70' : (STATUS_DOT[o.orderStatus ?? ''] ?? 'bg-gray-300');
                // 多天中间日用方形，开始/结束日用圆形加特殊颜色
                if (role === 'mid') {
                  return <span key={idx} className={`w-3 h-1 rounded-sm shrink-0 ${isSelected ? 'bg-white/50' : 'bg-teal-300/60'}`} />;
                }
                if (role === 'start') {
                  return <span key={idx} className={`w-2 h-2 rounded-full shrink-0 ring-1 ${isSelected ? 'ring-white/50 bg-white/70' : 'ring-teal-400 ' + dotColor}`} />;
                }
                return <span key={idx} className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />;
              })}
              {dayOrders.length > 3 && (
                <span className={`text-[9px] font-bold leading-none ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                  +{dayOrders.length - 3}
                </span>
              )}
            </div>
          )}
          {/* 多天活动连续指示条 */}
          {isCurrentMonth && hasMultiDayMid && !isSelected && (
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-teal-200/70 rounded-full" />
          )}
          {isCurrentMonth && hasMultiDayStart && !isSelected && (
            <div className="absolute bottom-0 left-1/2 right-0 h-[2px] bg-teal-300/70 rounded-full" />
          )}
        </div>
      );
    }
    return cells;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm md:text-base font-semibold text-gray-900">活动日历</h3>
          <div className="flex items-center gap-1">
            {loading && <i className="ri-loader-4-line animate-spin text-sm text-gray-400 mr-1" />}
            <button
              onClick={previousMonth}
              className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-base" />
            </button>
            <span className="text-sm font-medium text-gray-700 px-2 whitespace-nowrap">
              {currentDate.getFullYear()}年 {MONTH_NAMES[currentDate.getMonth()]}
            </span>
            <button
              onClick={nextMonth}
              className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-base" />
            </button>
          </div>
        </div>

        {/* Week headers */}
        <div className="grid grid-cols-7 mb-1">
          {WEEK_DAYS.map(d => (
            <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {renderDays()}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50 flex-wrap">
          {[['未执行', 'bg-amber-400'], ['执行中', 'bg-cyan-400'], ['已完成', 'bg-teal-400']].map(([label, bg]) => (
            <div key={label} className="flex items-center gap-1 text-[11px] text-gray-400">
              <span className={`w-2 h-2 rounded-full ${bg}`} />
              {label}
            </div>
          ))}
          <div className="flex items-center gap-1 text-[11px] text-gray-400">
            <span className="w-3 h-1 rounded-sm bg-teal-300/60" />
            多天活动
          </div>
          <div className="ml-auto text-[11px] text-gray-400">
            本月 <strong className="text-gray-600">{new Set(Object.values(ordersByDate).flat().map(o => o.id)).size}</strong> 单
          </div>
        </div>
      </div>

      {/* Selected date orders panel */}
      {selectedDate && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500">
                {selectedDate.replace(/-/g, '年').replace(/年(\d+)年/, '年$1月').replace(/月(\d+)$/, '月$1日')} 的订单
              </p>
              {selectedOrders.length > 0 && (
                <button
                  onClick={() => navigate('/orders', { state: { filterStartDate: selectedDate } })}
                  className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors flex items-center gap-0.5 whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-s-line text-xs" />
                </button>
              )}
            </div>

            {selectedOrders.length === 0 ? (
              <div className="text-center py-5 text-gray-400">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-2">
                  <i className="ri-calendar-line text-base text-gray-300" />
                </div>
                <p className="text-xs">当天暂无订单</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedOrders.map(o => {
                  const badgeCls = STATUS_BADGE[o.orderStatus ?? ''] ?? 'bg-gray-100 text-gray-500 border-gray-200';
                  const isMultiDay = o.orderEndDate && o.orderEndDate !== o.orderStartDate;
                  const role = getDayRole(o, selectedDate);
                  return (
                    <div key={o.id} className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3 cursor-pointer hover:border-teal-200 transition-colors" onClick={() => navigate('/orders', { state: { openOrderId: o.id } })}>
                      <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[o.orderStatus ?? ''] ?? 'bg-gray-300'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-semibold text-gray-800 truncate">
                            {o.customerName ?? o.contactPerson ?? '未知客户'}
                          </p>
                          {o.serviceTypeName && (
                            <span className="text-[10px] text-gray-400 shrink-0">{o.serviceTypeName}</span>
                          )}
                          {isMultiDay && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${
                              role === 'start' ? 'bg-teal-100 text-teal-600' :
                              role === 'end' ? 'bg-amber-100 text-amber-600' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {role === 'start' ? '开始' : role === 'end' ? '结束' : '进行中'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-mono text-gray-400">
                          {o.orderNumber ?? o.id.slice(0, 8).toUpperCase()}
                          {isMultiDay && ` · ${o.orderStartDate.slice(5)} ~ ${o.orderEndDate!.slice(5)}`}
                          {o.orderAmount != null && ` · ¥${Number(o.orderAmount).toLocaleString('zh-CN')}`}
                        </p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium shrink-0 whitespace-nowrap ${badgeCls}`}>
                        {o.orderStatus}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
